// @ts-check

import {LitElement, css, html} from 'lit';
import { customElement} from 'lit/decorators.js';
export class VoyageTooltip extends LitElement
{
    constructor()
    {
        super();
        
        this.for = /** @type {HTMLElement|null} */(null);
        this.shown = false;
        this.position = "top";
        this.offset = 0;
        this.manualVisibility = false;
        this.manualPositioning = false;
        this.positionPriority = ["top", "right", "left", "bottom"];
        this.tooltipInteraction = false;

        this._internalOffset = 8;

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    static get properties()
    {
        return {
            for: { attribute: false },
            shown: { type: Boolean, reflect: true },
            position: { type: String, reflect: true },
            offset: { type: Number },
            positionPriority: { type: Array, attribute: "position-priority" },
            manualVisibility: { type: Boolean, attribute: "manual-visibility" },
            manualPositioning: { type: Boolean, attribute: "manual-positioning" },
            tooltipInteraction: { type: Boolean, reflect: true }
        }
    }

    static get styles() {
        return [
            css`
            :host {
                font-family: MaerskText;
                line-height: 20px;
                font-size: 14px;
                position: absolute;
                box-sizing: border-box;
                max-width: 260px;
				opacity: 0;
				background-color: var(--tooltip-background-color, var(--neutrals-grey-02, grey));
				border-radius: var(--tooltip-border-radius, 5px);
				padding: 12px 16px 12px 16px;
				color: var(--tooltip-text-color, var(--neutrals-grey-10, white));
				user-select: none;
                transition: opacity 0.5s linear;
                display: block;
                pointer-events: none;
			}
			:host([shown]) {
                opacity: 1;
                pointer-events: auto;
            }
			:host:before {
				content: "";
				position: absolute;
				border-width: 6px;
				border-style: solid;
				user-select: none;
			}
            :host([tooltipInteraction]) {
                user-select: auto;
            }
			:host([position="top"]):before {
				transform: translateX(var(--tooltip-adjustment, 0px)) translateX(-50%) rotate(45deg);
				border-color: var(--tooltip-background-color, var(--neutrals-grey-02, grey));
				bottom: -5.5px;
				left: 50%;
			}
			:host([position="bottom"]):before {
				transform: translateX(var(--tooltip-adjustment, 0px)) translateX(-50%) rotate(45deg);
				border-color: var(--tooltip-background-color, var(--neutrals-grey-02, grey));
				top: -5.5px;
				left: 50%;
			}
            :host([position="left"]):before {
				transform: translateY(var(--tooltip-adjustment, 0px)) translateY(-50%) rotate(45deg);
				border-color: var(--tooltip-background-color, var(--neutrals-grey-02, grey));
				top: 50%;
				right: -5.5px;
			}
            :host([position="right"]):before {
				transform: translateY(var(--tooltip-adjustment, 0px)) translateY(-50%) rotate(45deg);
				border-color: var(--tooltip-background-color, var(--neutrals-grey-02, grey));
				top: 50%;
				left: -5.5px;
			}
            .backdrop {
                display: none;
            }
            .backdrop[show] {
                display: block;
            }
            :host([position="top"]) .backdrop {
                position: absolute;
                height: 20px;
                width: 100%;
                bottom: -20px;
                left: 0px;
            }
            :host([position="bottom"]) .backdrop {
                position: absolute;
                height: 20px;
                width: 100%;
                left: 0px;
                top: -20px;
            }
            :host([position="left"]) .backdrop {
                position: absolute;
                height: 100%;
                width: 20px;
                top: 0px;
                right: -20px;
            }
            :host([position="right"]) .backdrop {
                position: absolute;
                height: 100%;
                width: 20px;
                top: 0px;
                left: -20px;
            }
            `
        ];
    }

    render()
    {
        this._position();
        return html`
            <div class="backdrop" ?show=${this.tooltipInteraction}></div>
            <slot></slot>
        `;
    }

    updated(changedProperties)
    {
        changedProperties.forEach( (oldValue, propName) => {
            if (propName === "for" || propName === "manualVisibility")
            {
                this._removeEventListeners(propName === "for" ? oldValue : this.for);
                this._addEventListeners(this.for);
            }
        });
    }

    connectedCallback()
    {
        super.connectedCallback();
        this._addEventListeners(this.for);
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        this._removeEventListeners(this.for);
    }

    show()
    {
        this.shown = true;
    }

    hide()
    {
        this.shown = false;
    }

    _position()
    {
        if (this.shown && this.for && !this.manualPositioning)
        {
            const forRect = this.for.getBoundingClientRect();
            const offsetParentRect =  this.offsetParent && this.offsetParent.getBoundingClientRect();
            let valid = false;
            for (const position of this.positionPriority)
            {
                valid = this._tryPosition(position, forRect, offsetParentRect);
                if (valid)
                {
                    break;
                }
            }
            if (!valid)
            {
                console.log("Could not find a valid position to place tooltip in! Tooltip will not be shown");
                this.hide();
            }
        }
    }

    _tryPosition(position, targetRect, offsetParentRect)
    {
        const targetLeft = targetRect.left - offsetParentRect.left;
        const targetTop = targetRect.top - offsetParentRect.top;
        const offset = this._internalOffset + this.offset;

        const rect = this.getBoundingClientRect();
        let left = 0;
        let top = 0;
        let adjustment = 0;

        switch (position)
        {
            case "top":
                left = targetLeft + (targetRect.width - rect.width)/2;
                top = targetTop - rect.height - offset;
                break;

            case "right":
                left = targetLeft + targetRect.width + offset;
                top = targetTop + (targetRect.height - rect.height)/2;
                break;

            case "left":
                left = targetLeft - rect.width - offset;
                top = targetTop + (targetRect.height - rect.height)/2;
                break;

            case "bottom":
                left = targetLeft + (targetRect.width - rect.width)/2;
                top = targetTop + targetRect.height + offset;
                break;
        }
        
        // Adjust along the left/right (for top/bottom position) or up/down (for left/right position) axis if needed
        if (position === "top" || position === "bottom")
        {
            if (left < 0)
            {
                adjustment = left;
                left = 0;
            }
            else if ((left + rect.width) > window.innerWidth)
            {
                adjustment = left - (window.innerWidth - rect.width);
                left = window.innerWidth - rect.width;
            }
        }
        else
        {
            if (top < 0)
            {
                adjustment = top;
                top = 0;
            }
            else if ((top + rect.height) > window.innerHeight)
            {
                adjustment = top - (window.innerWidth - rect.height);
                top = window.innerHeight - rect.height;
            }
        }
        this.style.setProperty("--tooltip-adjustment", `${adjustment}px`);

        // Check if position would be valid (ie. stay on screen) and apply position if it is
        const valid = top >= 0 && top + rect.height <= window.innerHeight && left >= 0 && left + rect.width <= window.innerWidth
        if (valid)
        {
            this.position = position;
            this.setAttribute("position", position); // Need to explicitly set the attribute since this function is run inside render()
            this.style.setProperty("top", `${top}px`);
            this.style.setProperty("left", `${left}px`);
        }
        return valid;
    }

    _addEventListeners(target)
    {
        if (target && this.tooltipInteraction) {
            this.addEventListener('mouseenter', this.show);
            this.addEventListener('mouseleave', this.hide);
        }
        if (target && !this.manualVisibility)
        {
            target.addEventListener('mouseenter', this.show);
            target.addEventListener('mouseleave', this.hide);
        }
    }

    _removeEventListeners(target)
    {
        if (target)
        {
            target.removeEventListener('mouseenter', this.show);
            target.removeEventListener('mouseleave', this.hide);    
        }
    }
};

customElements.define('voyage-tooltip', VoyageTooltip);
